import urlConstants from '~/lib/constants/url.constants';
import api from './api';
import { ENUMERATION_CACHE_TIME } from './enumerations';
import { HttpRequestMethod } from '..';

interface AccountsRequest {
  providerExternalIds: string[];
}

interface GetAttendeesRequest {
  patientId: string;
  appointmentTypeGroup: string;
  treatmentProgram: string;
}
interface GetProviderAvailabilityRequest {
  appointmentType: string;
  frequency?: string;
  ignoreAvailabilitySettings?: boolean;
  start: string;
  end: string;
  minimumProviderCount: number;
  patientId?: string;
  providerIds: string[];
  strictAvailabilityRule?: boolean;
  supportIds?: string[];
}

interface GetProviderAvailabilityRequestV2 {
  appointmentType: string;
  start: string;
  end: string;
  frequency?: string;
  includeConflicts?: boolean;
  ignoreAvailabilitySettings?: boolean;
  minimumProviderCount?: number;
  patientId?: string;
  inquiryId?: string;
  providerIds?: string[];
  strictAvailabilityRule?: boolean;
  supportIds?: string[];
  limit?: number;
}

export interface GetProviderAvailabilityResponse {
  data: {
    availableSlots: {
      start: string;
      end: string;
      users: { id: string }[];
    }[];
  };
}

export interface GetProviderAvailabilityResponseV2 {
  availableSlots: {
    start: string;
    end: string;
    users: { id: string }[];
  }[];
}

export const scheduleApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAppointmentTypes: build.query<AppointmentType[], string | void>({
      query: (name?: string | undefined) => ({
        url: urlConstants.calendar.getAppointmentTypes(name),
        method: HttpRequestMethod.GET,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getAccounts: build.query<CalendarAccount[], AccountsRequest>({
      query: (request: AccountsRequest) => ({
        url: urlConstants.calendar.getAccounts,
        method: HttpRequestMethod.GET,
        params: {
          'provider-external-ids': request.providerExternalIds,
        },
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getAttendees: build.query<AttendeesResponse, GetAttendeesRequest>({
      query: ({ patientId, appointmentTypeGroup, treatmentProgram }) => ({
        method: HttpRequestMethod.GET,
        params: {
          'appointment-type-group': appointmentTypeGroup,
          'patient-id': patientId,
          'treatment-program': treatmentProgram,
        },
        url: urlConstants.calendar.getAttendees,
      }),
    }),
    getProviderAvailability: build.query<
      GetProviderAvailabilityResponse,
      GetProviderAvailabilityRequest
    >({
      query: ({
        appointmentType,
        frequency = 'does-not-repeat',
        ignoreAvailabilitySettings = false,
        start,
        end,
        minimumProviderCount,
        providerIds,
        patientId,
        strictAvailabilityRule = false,
        supportIds,
      }) => {
        // Deduplicate provider IDs to prevent "participant specified multiple times" error
        const uniqueProviderIds = [...new Set(providerIds)];

        const params = {
          'appointment-type': appointmentType,
          end,
          frequency,
          'ignore-availability-settings': ignoreAvailabilitySettings,
          'minimum-provider-count': minimumProviderCount,
          'provider-ids': uniqueProviderIds.join(','),
          start,
          'strict-availability-rule': strictAvailabilityRule,
        };
        if (patientId) {
          // Check if patient ID is in the provider list (would cause duplicate participant error)
          if (uniqueProviderIds.includes(patientId)) {
            console.error(
              '[getProviderAvailability] Patient ID found in provider list!',
              {
                patientId,
                providerIds: uniqueProviderIds,
              },
            );
            // Don't add patient-id to avoid duplicate participant error
          } else {
            params['patient-id'] = patientId;
          }
        }
        if (supportIds?.length) {
          // Deduplicate support IDs and remove any that are already in provider IDs or match patient ID
          const uniqueSupportIds = [...new Set(supportIds)].filter(
            (id) => !uniqueProviderIds.includes(id) && id !== patientId,
          );
          if (supportIds.length !== uniqueSupportIds.length) {
            console.warn(
              '[getProviderAvailability] Duplicate or overlapping support IDs:',
              {
                original: supportIds,
                unique: uniqueSupportIds,
                removed: supportIds.filter(
                  (id) => !uniqueSupportIds.includes(id),
                ),
              },
            );
          }
          if (uniqueSupportIds.length > 0) {
            params['support-ids'] = uniqueSupportIds.join(',');
          }
        }

        return {
          method: HttpRequestMethod.GET,
          params,
          url: urlConstants.calendar.getProviderAvailability,
        };
      },
    }),

    getProviderAvailabilityV2: build.query<
      GetProviderAvailabilityResponseV2,
      GetProviderAvailabilityRequestV2
    >({
      query: ({ appointmentType, start, end, limit, patientId, inquiryId }) => {
        const params = {
          'appointment-type': appointmentType,
          end,
          start,
          ...(typeof limit === 'number' ? { limit } : {}),
          ...(patientId ? { 'patient-id': patientId } : {}),
          ...(inquiryId ? { 'inquiry-id': inquiryId } : {}),
        };

        return {
          method: HttpRequestMethod.GET,
          params,
          url: urlConstants.calendar.getProviderAvailabilityV2,
        };
      },
    }),
  }),
});

export const {
  useLazyGetAppointmentTypesQuery,
  useGetAppointmentTypesQuery,
  useLazyGetAccountsQuery,
  useGetProviderAvailabilityQuery,
  useLazyGetProviderAvailabilityQuery,
  useGetAttendeesQuery,
  useLazyGetAttendeesQuery,
  useGetProviderAvailabilityV2Query,
} = scheduleApi;
